import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { Upload, FileText, CheckCircle, AlertCircle, Download, RotateCcw } from 'lucide-react';
import DocumentTypeSelector from './DocumentTypeSelector';
import ResultsDisplay from './ResultsDisplay';
import LoadingSpinner from './LoadingSpinner';
import { UploadPageProps, DocumentType, UploadResponse, ComparisonResponse, RegistrationResponse, VerificationResponse } from '../types';
import ApiService from '../services/apiService';
import ComparisonDisplay from './ComparisonDisplay';
import RegistrationDisplay from './RegistrationDisplay';
import VerificationDisplay from './VerificationDisplay';

const UploadPage: React.FC<UploadPageProps> = ({ documentTypes, onUpload, onCompare }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<any>(null);
  const [comparisonResults, setComparisonResults] = useState<ComparisonResponse['data'] | null>(null);
  const [registrationResults, setRegistrationResults] = useState<RegistrationResponse['data'] | null>(null);
  const [verificationResults, setVerificationResults] = useState<VerificationResponse['data'] | null>(null);
  const [verificationFiles, setVerificationFiles] = useState<{ [key: string]: File | null }>({
    austrian_passport: null,
    austrian_id_card: null,
    real_estate_contract: null
  });
  const [processingTime, setProcessingTime] = useState(0);
  const [confidence, setConfidence] = useState(0);

  const selectedType = documentTypes.find(type => type.id === selectedDocumentType);
  const isComparisonType = selectedType?.id === 'angebotsvergleich';
  const isRegistrationType = selectedType?.id === 'angebotserfassung';
  const isVerificationType = selectedType?.id === 'document_verification';

  const verificationDocumentTypes = [
    { id: 'austrian_passport', label: 'Österreichischer Reisepass' },
    { id: 'austrian_id_card', label: 'Österreichische ID-Karte' },
    { id: 'real_estate_contract', label: 'Immobilien-Kaufvertrag' }
  ];

  const validateFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    if (!selectedType) {
      return { isValid: false, error: 'Please select a document type first' };
    }

    return ApiService.validateFile(file, selectedType);
  }, [selectedType]);

  const validateFiles = useCallback((files: File[]): { isValid: boolean; error?: string } => {
    if (!selectedType) {
      return { isValid: false, error: 'Please select a document type first' };
    }

    // Check file count for comparison or registration
    if (isComparisonType || isRegistrationType) {
      if (files.length < (selectedType.minFiles || 1)) {
        return { isValid: false, error: `Minimum ${selectedType.minFiles || 1} files required` };
      }
      if (files.length > (selectedType.maxFiles || 3)) {
        return { isValid: false, error: `Maximum ${selectedType.maxFiles || 3} files allowed` };
      }
    }

    // Validate each file
    for (const file of files) {
      const validation = ApiService.validateFile(file, selectedType);
      if (!validation.isValid) {
        return validation;
      }
    }

    return { isValid: true };
  }, [selectedType, isComparisonType]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    if (isComparisonType || isRegistrationType) {
      // Handle multiple files for comparison or registration
      const validation = validateFiles(acceptedFiles);
      
      if (!validation.isValid) {
        setError(validation.error!);
        toast.error(validation.error!);
        return;
      }

      setSelectedFiles(acceptedFiles);
      setSelectedFile(null);
      setError(null);
      const action = isRegistrationType ? 'registration' : 'comparison';
      toast.success(`${acceptedFiles.length} files selected for ${action}`);
    } else {
      // Handle single file
      const file = acceptedFiles[0];
      const validation = validateFile(file);
      if (!validation.isValid) {
        setError(validation.error!);
        toast.error(validation.error!);
        return;
      }

      setSelectedFile(file);
      setSelectedFiles([]);
      setError(null);
      toast.success(`File "${file.name}" selected successfully`);
    }
  }, [validateFile, validateFiles, isComparisonType, isRegistrationType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: selectedType ? {
      ...ApiService.getMimeTypesForDocumentType(selectedType).reduce((acc, mime) => {
        acc[mime] = [];
        return acc;
      }, {} as Record<string, string[]>)
    } : undefined,
    multiple: isComparisonType || isRegistrationType,
    maxFiles: (isComparisonType || isRegistrationType) ? (selectedType?.maxFiles || 3) : 1,
    disabled: !selectedDocumentType
  });

  const handleDocumentTypeChange = useCallback((typeId: string) => {
    setSelectedDocumentType(typeId);
    setSelectedFile(null);
    setSelectedFiles([]);
    setError(null);
    setResults(null);
    setComparisonResults(null);
    setRegistrationResults(null);
    setVerificationResults(null);
    setVerificationFiles({
      austrian_passport: null,
      austrian_id_card: null,
      real_estate_contract: null
    });
  }, []);

  const handleSubmit = async () => {
    if (isComparisonType) {
      // Handle comparison upload
      if (!selectedFiles.length || !selectedDocumentType) {
        toast.error('Please select files and document type for comparison');
        return;
      }

      if (!onCompare) {
        toast.error('Comparison functionality not available');
        return;
      }

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      setComparisonResults(null);

      const toastId = toast.loading('Processing documents for comparison...');

      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 3, 90));
        }, 500);

        const startTime = Date.now();
        const response: ComparisonResponse = await onCompare(selectedFiles, selectedDocumentType);
        const endTime = Date.now();

        clearInterval(progressInterval);
        setUploadProgress(100);
        setProcessingTime(endTime - startTime);

        if (response.success && response.data) {
          setComparisonResults(response.data);
          setConfidence(response.data.confidence);
          toast.success('Documents compared successfully!', { id: toastId });
        } else {
          const errorMessage = response.error?.message || 'Comparison failed';
          setError(errorMessage);
          toast.error(errorMessage, { id: toastId });
        }
      } catch (err: any) {
        console.error('Comparison error:', err);
        const errorMessage = err.message || 'An unexpected error occurred';
        setError(errorMessage);
        toast.error(errorMessage, { id: toastId });
      } finally {
        setIsUploading(false);
      }
    } else if (isRegistrationType) {
      // Handle registration upload
      if (!selectedFiles.length || !selectedDocumentType) {
        toast.error('Please select files and document type for registration');
        return;
      }

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      setRegistrationResults(null);

      const toastId = toast.loading('Processing documents for registration...');

      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 3, 90));
        }, 500);

        const startTime = Date.now();
        const response: RegistrationResponse = await ApiService.registerDocuments(selectedFiles, selectedDocumentType);
        const endTime = Date.now();

        clearInterval(progressInterval);
        setUploadProgress(100);
        setProcessingTime(endTime - startTime);

        if (response.success && response.data) {
          setRegistrationResults(response.data);
          setConfidence(response.data.confidence);
          
          if (response.data.databaseSave.success) {
            toast.success(`Documents registered successfully! ${response.data.databaseSave.savedCount} offers saved to database.`, { id: toastId });
          } else {
            toast.success('Documents processed successfully, but database save failed. Data is still available for download.', { id: toastId });
          }
        } else {
          const errorMessage = response.error?.message || 'Registration failed';
          setError(errorMessage);
          toast.error(errorMessage, { id: toastId });
        }
      } catch (err: any) {
        console.error('Registration error:', err);
        const errorMessage = err.message || 'An unexpected error occurred';
        setError(errorMessage);
        toast.error(errorMessage, { id: toastId });
      } finally {
        setIsUploading(false);
      }
    } else if (isVerificationType) {
      // Handle verification upload
      const documentsToVerify = Object.entries(verificationFiles)
        .filter(([_, file]) => file !== null)
        .map(([docType, file]) => ({ file: file!, documentType: docType }));

      if (documentsToVerify.length === 0) {
        toast.error('Bitte laden Sie mindestens ein Dokument hoch');
        return;
      }

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      setVerificationResults(null);

      const toastId = toast.loading('Dokumente werden verifiziert...');

      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 2, 90));
        }, 500);

        const startTime = Date.now();
        const response: VerificationResponse = await ApiService.verifyDocuments(documentsToVerify);
        const endTime = Date.now();

        clearInterval(progressInterval);
        setUploadProgress(100);
        setProcessingTime(endTime - startTime);

        if (response.success && response.data) {
          setVerificationResults(response.data);
          
          if (response.data.overallVerified) {
            toast.success('Alle Dokumente wurden erfolgreich verifiziert!', { id: toastId });
          } else {
            toast.success('Verifizierung abgeschlossen. Einige Dokumente konnten nicht vollständig verifiziert werden.', { id: toastId });
          }
        } else {
          const errorMessage = response.error?.message || 'Verifizierung fehlgeschlagen';
          setError(errorMessage);
          toast.error(errorMessage, { id: toastId });
        }
      } catch (err: any) {
        console.error('Verification error:', err);
        const errorMessage = err.message || 'Ein unerwarteter Fehler ist aufgetreten';
        setError(errorMessage);
        toast.error(errorMessage, { id: toastId });
      } finally {
        setIsUploading(false);
      }
    } else {
      // Handle single file upload
      if (!selectedFile || !selectedDocumentType) {
        toast.error('Please select both a file and document type');
        return;
      }

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);
      setResults(null);

      const toastId = toast.loading('Processing document...');

      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 5, 90));
        }, 300);

        const startTime = Date.now();
        const response: UploadResponse = await onUpload(selectedFile, selectedDocumentType);
        const endTime = Date.now();

        clearInterval(progressInterval);
        setUploadProgress(100);
        setProcessingTime(endTime - startTime);

        if (response.success && response.data) {
          setResults(response.data.extractedData);
          setConfidence(response.data.confidence);
          toast.success('Document processed successfully!', { id: toastId });
        } else {
          const errorMessage = response.error?.message || 'Processing failed';
          setError(errorMessage);
          toast.error(errorMessage, { id: toastId });
        }
      } catch (err: any) {
        console.error('Upload error:', err);
        const errorMessage = err.message || 'An unexpected error occurred';
        setError(errorMessage);
        toast.error(errorMessage, { id: toastId });
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleDownload = () => {
    const dataToDownload = verificationResults || comparisonResults || registrationResults || results;
    if (!dataToDownload) return;

    try {
      const dataStr = JSON.stringify(dataToDownload, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `extracted_data_${selectedDocumentType}_${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
      toast.success('JSON file downloaded successfully');
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setSelectedFiles([]);
    setSelectedDocumentType('');
    setError(null);
    setResults(null);
    setComparisonResults(null);
    setRegistrationResults(null);
    setVerificationResults(null);
    setVerificationFiles({
      austrian_passport: null,
      austrian_id_card: null,
      real_estate_contract: null
    });
    setUploadProgress(0);
    setProcessingTime(0);
    setConfidence(0);
  };

  const handleVerificationFileSelect = (docType: string, file: File | null) => {
    setVerificationFiles(prev => ({
      ...prev,
      [docType]: file
    }));
    setError(null);
  };

  const canSubmit = isVerificationType
    ? (Object.values(verificationFiles).some(file => file !== null) && selectedDocumentType && !isUploading && !error)
    : (isComparisonType || isRegistrationType)
    ? (selectedFiles.length > 0 && selectedDocumentType && !isUploading && !error && (isComparisonType ? onCompare : true))
    : (selectedFile && selectedDocumentType && !isUploading && !error);


  // Show comparison results
  if (comparisonResults && selectedType) {
    return (
      <ComparisonDisplay
        data={comparisonResults}
        onDownload={handleDownload}
        onReset={handleReset}
      />
    );
  }

  // Show registration results
  if (registrationResults && selectedType) {
    return (
      <RegistrationDisplay
        data={registrationResults}
        onDownload={handleDownload}
        onReset={handleReset}
      />
    );
  }

  // Show verification results
  if (verificationResults && selectedType) {
    return (
      <VerificationDisplay
        data={verificationResults}
        onDownload={handleDownload}
        onReset={handleReset}
      />
    );
  }

  // Show single file results
  if (results && selectedType) {
    return (
      <ResultsDisplay
        data={results}
        documentType={selectedType.name}
        confidence={confidence}
        processingTime={processingTime}
        onDownload={handleDownload}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Dokument hochladen
          </h2>
          <p className="text-gray-600">
            Laden Sie ein Dokument hoch und wählen Sie den Typ aus, um strukturierte Daten mit KI zu extrahieren
          </p>
        </div>

        {/* Document Type Selection */}
        <div className="mb-6">
          <DocumentTypeSelector
            documentTypes={documentTypes}
            selectedType={selectedDocumentType}
            onTypeChange={handleDocumentTypeChange}
          />
        </div>

        {/* File Upload Area */}
        {isVerificationType ? (
          <div className="mb-6 space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Laden Sie die entsprechenden Dokumente für jeden Typ hoch:
            </p>
            {verificationDocumentTypes.map((docType) => {
              const file = verificationFiles[docType.id];
              return (
                <div key={docType.id} className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {docType.label}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => {
                      const selectedFile = e.target.files?.[0] || null;
                      handleVerificationFileSelect(docType.id, selectedFile);
                    }}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-primary-50 file:text-primary-700
                      hover:file:bg-primary-100
                      cursor-pointer"
                  />
                  {file && (
                    <div className="mt-2 flex items-center justify-between p-2 bg-success-50 rounded">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-success-600" />
                        <span className="text-sm font-medium text-success-900">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({ApiService.formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        onClick={() => handleVerificationFileSelect(docType.id, null)}
                        className="text-error-600 hover:text-error-800 text-sm"
                      >
                        Entfernen
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mb-6">
            <div
              {...getRootProps()}
              className={`dropzone cursor-pointer transition-colors duration-200 ${
                isDragActive ? 'dropzone-active' : ''
              } ${!selectedDocumentType ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center">
                {selectedFile || selectedFiles.length > 0 ? (
                  <>
                    <CheckCircle className="h-12 w-12 text-success-500 mb-4" />
                    <p className="text-lg font-medium text-success-700 mb-2">
                      {isComparisonType ? `${selectedFiles.length} Dateien ausgewählt` : 'Datei ausgewählt'}
                    </p>
                    <div className="text-center">
                      {isComparisonType ? (
                        <div className="space-y-2">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="text-sm">
                              <p className="font-medium text-gray-900">{file.name}</p>
                              <p className="text-gray-500">{ApiService.formatFileSize(file.size)}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <p className="font-medium text-gray-900">{selectedFile?.name}</p>
                          <p className="text-sm text-gray-500">
                            {selectedFile && ApiService.formatFileSize(selectedFile.size)}
                          </p>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">
                      {selectedDocumentType
                        ? (isComparisonType 
                            ? 'Ziehen Sie 2-3 Darlehensangebote hierher oder klicken Sie zum Durchsuchen'
                            : isRegistrationType
                            ? 'Ziehen Sie 1-3 Darlehensangebote hierher oder klicken Sie zum Durchsuchen'
                            : 'Ziehen Sie Ihr Dokument hierher oder klicken Sie zum Durchsuchen')
                        : 'Wählen Sie zuerst einen Dokumententyp aus'
                      }
                    </p>
                    {selectedDocumentType && (
                      <p className="text-sm text-gray-500">
                        Unterstützte Formate: {selectedType?.supportedFormats.join(', ').toUpperCase()}
                        <br />
                        Maximale Größe: {(selectedType?.maxFileSize || 0) / 1024 / 1024}MB
                        {(isComparisonType || isRegistrationType) && (
                          <>
                            <br />
                            Anzahl Dateien: {selectedType?.minFiles || 1} - {selectedType?.maxFiles || 3}
                          </>
                        )}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-error-500 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-error-800 font-medium">Upload-Fehler</p>
                <p className="text-error-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="mb-6">
            <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-primary-600 h-full transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <div className="flex justify-between items-center mt-2">
              <p className="text-sm text-gray-600">
                {uploadProgress < 100 ? 'Dokument wird verarbeitet...' : 'Fertig!'}
              </p>
              <p className="text-sm font-medium text-primary-600">
                {uploadProgress}%
              </p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary px-8 py-3 text-lg flex items-center space-x-2"
          >
            {isUploading ? (
              <>
                <LoadingSpinner />
                <span>{isComparisonType ? 'Vergleiche...' : isRegistrationType ? 'Erfasse...' : isVerificationType ? 'Verifiziere...' : 'Verarbeitung...'}</span>
              </>
            ) : (
              <>
                <FileText className="h-5 w-5" />
                <span>{isComparisonType ? 'Angebote vergleichen' : isRegistrationType ? 'Angebote erfassen' : isVerificationType ? 'Dokumente verifizieren' : 'Daten extrahieren'}</span>
              </>
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Benötigen Sie Hilfe? Stellen Sie sicher, dass Ihr Dokument klar und lesbar ist, um die besten Ergebnisse zu erzielen.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
